import React from "react";
import { Outlet, Link } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  VercelThemeProvider,
  useVercelTheme,
} from "@/components/admin/VercelThemeProvider";
import GlobalToaster from "@/components/GlobalToaster";
import { ThemeToggle } from "@/components/shared/ThemeToggle";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { CookieConsentBanner } from "@/components/shared/CookieConsentBanner";
import { useOperationRegion } from "@/hooks/useOperationRegion";
import { getLogoUrl } from "@/components/nav/NavLogo"; // This import is already correct
import { BootstrapProvider } from "@/components/providers/BootstrapProvider";

/**
 * 认证页面内部布局（在 ThemeProvider 内部使用）
 */
function AuthLayoutInner() {
  const { resolvedTheme } = useVercelTheme();
  const { region, isInternational, loading } = useOperationRegion();

  return (
    <div className="min-h-dvh bg-white bg-[url('/images/bg-white.png')] bg-repeat bg-fixed dark:bg-black dark:bg-[url('/images/bg-dark.png')] text-foreground flex flex-col">
      <GlobalToaster />
      {/* 顶部导航栏 - 透明背景，让背景图片穿透 */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-sm">
        <div className="relative flex justify-between items-center h-16 px-4 max-w-7xl mx-auto">
          <Link to="/" className="flex items-center">
            <img
              src={getLogoUrl(region, resolvedTheme)}
              alt="吞舟 TUNZO"
              className="h-8"
            />
          </Link>
          <div className="flex items-center gap-2 px-2">
            {/* 海外模式显示语言切换器，国内模式隐藏 */}
            {!loading && isInternational && <LanguageSwitcher />}
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* 占位空间，避免内容被固定导航遮挡 */}
      <div className="h-16 shrink-0" />

      {/* 主内容区 */}
      <main className="flex-1 w-full flex flex-col">
        <Outlet />
      </main>

      {/* Cookie 同意横幅 - 仅国际版显示 */}
      <CookieConsentBanner />
    </div>
  );
}

/**
 * 认证页面布局组件
 * - 简化的头部（只有 logo、语言切换、主题切换）
 * - 不包含完整导航栏
 * - 支持亮色/暗色主题切换
 * - 包含 BootstrapProvider 以初始化区域配置
 *
 * 用于登录、注册、忘记密码等认证相关页面
 */
export default function AuthLayout() {
  return (
    <VercelThemeProvider defaultTheme="dark" storageKey="app-theme">
      <TooltipProvider>
        <BootstrapProvider>
          <AuthLayoutInner />
        </BootstrapProvider>
      </TooltipProvider>
    </VercelThemeProvider>
  );
}
